import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop"]

  get isDesktop() {
    return window.matchMedia("(min-width: 768px)").matches
  }

  open(event) {
    event.preventDefault()
    const panelId = event.currentTarget.dataset.sheet
    const panel = this.element.querySelector(`[data-panel-id="${panelId}"]`)
    if (!panel) return

    // Toggle: clicking the same trigger again closes the open panel
    if (this.activePanel === panel) {
      this.close()
      return
    }

    // Close any currently open panel first
    this.close()

    this.activePanel = panel
    this.activeTrigger = event.currentTarget

    if (this.isDesktop) {
      this.openInline(panel)
    } else {
      this.openSheet(panel)
    }
  }

  openInline(panel) {
    panel.classList.remove("hidden")
    // Focus first input if present
    const input = panel.querySelector("input:not([type=hidden]), select, textarea")
    if (input) setTimeout(() => input.focus(), 50)
  }

  openSheet(panel) {
    const sheetPanel = panel.querySelector("[data-role='sheet-panel']")
    panel.classList.remove("hidden")

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
      requestAnimationFrame(() => {
        this.backdropTarget.classList.remove("opacity-0")
        this.backdropTarget.classList.add("opacity-50")
        if (sheetPanel) {
          sheetPanel.classList.remove("translate-y-full")
          sheetPanel.classList.add("translate-y-0")
        }
      })
    }
  }

  close() {
    if (!this.activePanel) return

    if (this.isDesktop) {
      this.closeInline()
    } else {
      this.closeSheet()
    }
  }

  closeInline() {
    if (!this.activePanel) return
    this.activePanel.classList.add("hidden")
    this.activePanel = null
    this.activeTrigger = null
  }

  closeSheet() {
    if (!this.activePanel) return
    const panel = this.activePanel
    const sheetPanel = panel.querySelector("[data-role='sheet-panel']")

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-50")
      this.backdropTarget.classList.add("opacity-0")
    }
    if (sheetPanel) {
      sheetPanel.classList.remove("translate-y-0")
      sheetPanel.classList.add("translate-y-full")
    }
    document.body.classList.remove("overflow-hidden")

    const handler = () => {
      panel.classList.add("hidden")
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
      if (sheetPanel) sheetPanel.removeEventListener("transitionend", handler)
    }
    if (sheetPanel) {
      sheetPanel.addEventListener("transitionend", handler)
    } else {
      panel.classList.add("hidden")
    }

    this.activePanel = null
    this.activeTrigger = null
  }

  backdropClick(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  connect() {
    this._resetPanels()

    this._handleEscape = (e) => {
      if (e.key === "Escape") this.close()
    }
    this._handleMorph = () => this._resetPanels()
    document.addEventListener("keydown", this._handleEscape)
    document.addEventListener("turbo:morph", this._handleMorph)
  }

  disconnect() {
    document.removeEventListener("keydown", this._handleEscape)
    document.removeEventListener("turbo:morph", this._handleMorph)
    if (this.activePanel) {
      document.body.classList.remove("overflow-hidden")
    }
  }

  _resetPanels() {
    this.element.querySelectorAll("[data-panel-id]").forEach(panel => {
      panel.classList.add("hidden")
      const sheetPanel = panel.querySelector("[data-role='sheet-panel']")
      if (sheetPanel) {
        sheetPanel.classList.remove("translate-y-0")
        sheetPanel.classList.add("translate-y-full")
      }
    })
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden")
      this.backdropTarget.classList.remove("opacity-50")
      this.backdropTarget.classList.add("opacity-0")
    }
    document.body.classList.remove("overflow-hidden")
    this.activePanel = null
    this.activeTrigger = null
  }
}
